import { useDragDropMonitor, useDragOperation } from '@dnd-kit/react'
import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import { useRef } from 'react'
import { useContext } from 'use-context-selector'

import { t } from 'services/Translation'

import TestGroupingContext from '../../TestGroupingContext'
import IconLeft from '../../assets/icons-for-elements-left.svg?react'
import IconRight from '../../assets/icons-for-elements-right.svg?react'
import useCarouselFocus from '../../useCarouselFocus'
import GroupToolbar from '../GroupToolbar'
import * as s from './Stack.module.scss'
import StackCard from './StackCard'

interface IStackProps {
  items: TestGroupingItem[]
  groupValue: string
}

const Stack = ({ items, groupValue }: IStackProps) => {
  const { styles, font, destination, stackIndexes, setStackIndexes, selectedGroupId } =
    useContext(TestGroupingContext)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { source: dragSource } = useDragOperation()

  useDragDropMonitor({
    onDragEnd() {
      setStackIndexes((prev) => ({ ...prev, [groupValue]: 0 }))
    },
  })

  const group = destination.find((g) => g.value === groupValue)

  const carouselItems = [
    { type: 'group', data: group },
    ...items.map((item) => ({ type: 'answer', data: item })),
  ]
  const currentIndex = stackIndexes[groupValue] || 0
  const totalItems = carouselItems.length
  const shouldShowNav = totalItems > 1
  const navHintId = `${groupValue}-nav-hint`

  const handlePrevious = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setStackIndexes((prev) => ({
      ...prev,
      [groupValue]: (currentIndex + 1) % totalItems,
    }))
  }

  const handleNext = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setStackIndexes((prev) => ({
      ...prev,
      [groupValue]: currentIndex === 0 ? totalItems - 1 : currentIndex - 1,
    }))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (dragSource) {
      return
    }
    if (e.key === 'ArrowLeft') {
      handlePrevious(e)
    } else if (e.key === 'ArrowRight') {
      handleNext(e)
    }
  }

  useCarouselFocus(wrapperRef, currentIndex)

  if (!group) {
    return null
  }

  return (
    <div className={s.stackWrapper} style={styles.common}>
      <div
        className={s.stackContainer}
        onKeyDown={handleKeyDown}
        ref={wrapperRef}
        style={styles.common}
      >
        {shouldShowNav && (
          <span className={s.visuallyHidden} id={navHintId} hidden>
            {t('elements.grouping.accessibility.navHint')}
          </span>
        )}
        <AnimatePresence initial={false}>
          {carouselItems.map((carouselItem, index) => {
            const relativePosition = index - currentIndex
            const isActive = index === currentIndex
            if (carouselItem.type === 'group') {
              return (
                <>
                  <StackCard
                    ariaDescribedBy={shouldShowNav ? navHintId : undefined}
                    font={font}
                    group={group}
                    isActive={isActive}
                    relativePosition={relativePosition}
                    styles={styles}
                    type='group'
                  />
                  {selectedGroupId === group.value && <GroupToolbar />}
                </>
              )
            }

            const item = carouselItem.data as TestGroupingItem

            return (
              <StackCard
                font={font}
                group={group}
                isActive={isActive}
                item={item}
                key={item.value}
                relativePosition={relativePosition}
                styles={styles}
                type='answer'
              />
            )
          })}
        </AnimatePresence>
      </div>

      {shouldShowNav && (
        <>
          <div
            className={cn(s.navButton, s.prev)}
            onClick={handlePrevious}
            style={{ color: styles.common.color }}
          >
            <IconLeft fontSize={24} aria-hidden />
          </div>
          <div
            className={cn(s.navButton, s.next)}
            onClick={handleNext}
            style={{ color: styles.common.color }}
          >
            <IconRight fontSize={24} aria-hidden />
          </div>
        </>
      )}
    </div>
  )
}

export default Stack
